import React, { useEffect, useState } from 'react';
import { observabilityService } from '../../services/observability.service';
import {
  Download,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  RefreshCw,
  AlertCircle,
  Play,
  Pause
} from 'lucide-react';
import { format } from 'date-fns';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';

type SourceType = 'edge' | 'mtge';

type LogLevel = 'CRITICAL' | 'ERROR' | 'WARN' | 'WARNING' | 'INFO' | 'DEBUG' | 'ALL';

interface Edge {
  id: string;
  name: string;
  type?: 'edge' | 'mtge';
}

interface LogEntry {
  ts: string;
  line: string;
  level: string;
  parsed?: any;
  container?: string;
  system?: string;
  syslogPrefix?: string; // The non-JSON prefix part of a syslog line
}

const Logs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Source type and data
  const [sourceType, setSourceType] = useState<SourceType>('edge');
  const [availableEdges, setAvailableEdges] = useState<Edge[]>([]);
  const [availableMtges, setAvailableMtges] = useState<Edge[]>([]);

  // Filters
  const [source, setSource] = useState('');
  const [customSource, setCustomSource] = useState('');
  const [service, setService] = useState('all');
  const [levelFilter, setLevelFilter] = useState<LogLevel>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [timeRange, setTimeRange] = useState('1h');
  const [limit, setLimit] = useState(500);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Common services per device type
  const EDGE_SERVICES = ['agent-mqtt-cli', 'vpp-agent', 'alloy', 'suricata', 'strongswan', 'bird', 'syslog'];
  const MTGE_SERVICES = ['agent-mqtt-cli', 'vpp-agent', 'alloy', 'strongswan', 'bird', 'syslog'];
  const activeServices = sourceType === 'mtge' ? MTGE_SERVICES : EDGE_SERVICES;

  // UI state
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Fetch available edges from database (source of truth - edges register via /api/v1/vsr/)
  useEffect(() => {
    const fetchEdges = async () => {
      try {
        // Fetch from database - edges register and store their IDs here
        const res = await observabilityService.getRegisteredEdges();
        if (res.status === 'success' && res.data && res.data.length > 0) {
          // Sort edges by name for easier selection
          const sortedEdges = res.data.filter((e: any) => e.type !== 'mtge').sort((a: any, b: any) => a.name.localeCompare(b.name));
          setAvailableEdges(sortedEdges);
          if (sortedEdges.length > 0 && !source) setSource(sortedEdges[0].id);
          const sortedMtges = res.data.filter((e: any) => e.type === 'mtge').sort((a: any, b: any) => a.name.localeCompare(b.name));
          setAvailableMtges(sortedMtges);
        } else {
          // Fallback: try Loki if database doesn't have edges yet
          const lokiRes = await observabilityService.getLokiLabelValues('edge_id');
          if (lokiRes.status === 'success' && lokiRes.data && lokiRes.data.length > 0) {
            // Convert string array to Edge objects
            const fallbackEdges = lokiRes.data.map((id: string) => ({ id, name: id }));
            setAvailableEdges(fallbackEdges);
          } else {
            // Final fallback: Prometheus
            const promRes = await observabilityService.getLabelValues('edge_id');
            if (promRes.status === 'success' && promRes.data && promRes.data.length > 0) {
              // Convert string array to Edge objects
              const fallbackEdges = promRes.data.map((id: string) => ({ id, name: id }));
              setAvailableEdges(fallbackEdges);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch edges:', error);
        // Silently fail - user can still enter custom edge ID
      }
    };
    fetchEdges();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const end = Math.floor(Date.now() / 1000);
      let start = end - 3600;

      switch (timeRange) {
        case '5m': start = end - 300; break;
        case '15m': start = end - 900; break;
        case '1h': start = end - 3600; break;
        case '6h': start = end - 21600; break;
        case '24h': start = end - 86400; break;
      }

      // Construct LogQL query based on source type
      const activeSource = customSource || source;
      let query: string;

      // Query by edge_id (edges and MTGEs use the same label). The `service`
      // label is set per-source by edge alloy (agent-mqtt-cli emits it from
      // its JSON `service` field; other containers get a static label).
      if (activeSource && activeSource !== 'all') {
        if (service === 'all') {
          query = `{edge_id="${activeSource}"}`;
        } else if (service === 'syslog') {
          query = `{job="syslog", edge_id="${activeSource}"}`;
        } else {
          query = `{edge_id="${activeSource}", service=~"${service}.*"}`;
        }
      } else {
        if (service !== 'all') {
          query = `{edge_id=~".+", service=~"${service}.*"}`;
        } else {
          query = `{edge_id=~".+"}`; // Show all edge/MTGE logs
        }
      }

      // Add log level filter
      if (levelFilter !== 'ALL') {
        query += ` |~ "${levelFilter.toLowerCase()}|${levelFilter.toUpperCase()}"`;
      }

      // Add search query
      if (searchQuery) {
        query += ` |~ "${searchQuery}"`;
      }

      const res = await observabilityService.queryLogsRange(query, start, end, limit);

      if (res.status === 'success') {
        const flatLogs = res.data.result.flatMap(stream =>
          stream.values.map(([ts, line]) => {
            let level = 'INFO';
            let parsed = null;

            // Extract service or edge_id from stream labels
            const container = stream.stream?.service || stream.stream?.compose_service || stream.stream?.container || stream.stream?.edge_id || 'unknown';

            // Try to detect level
            const lineUpper = line.toUpperCase();
            if (lineUpper.includes('CRITICAL')) level = 'CRITICAL';
            else if (lineUpper.includes('ERROR')) level = 'ERROR';
            else if (lineUpper.includes('WARN')) level = 'WARN';
            else if (lineUpper.includes('DEBUG')) level = 'DEBUG';
            else if (lineUpper.includes('INFO')) level = 'INFO';

            // Try JSON parse - first check if line starts with JSON
            try {
              if (line.trim().startsWith('{')) {
                parsed = JSON.parse(line);
                if (parsed.level) level = parsed.level.toUpperCase();
              }
            } catch (e) { /* ignore */ }

            // If not pure JSON, try to extract embedded JSON from syslog-style lines
            // e.g. "2026-02-24T01:10:28... edgen100 docker[70160]: agent-mqtt-cli  | {\"customer_id\":...}"
            let syslogPrefix = '';
            if (!parsed) {
              const jsonStart = line.indexOf('{');
              if (jsonStart > 0) {
                try {
                  const jsonPart = line.substring(jsonStart);
                  parsed = JSON.parse(jsonPart);
                  syslogPrefix = line.substring(0, jsonStart).trim();
                  if (parsed.level) level = parsed.level.toUpperCase();
                } catch (e) { /* not valid JSON, ignore */ }
              }
            }

            // If still not parsed, reconstruct a structured view from stream labels.
            // Edge alloy strips the JSON envelope (stage.output { source = "message" }),
            // so the line is just the message but the labels carry the rest.
            if (!parsed && stream.stream && Object.keys(stream.stream).length > 0) {
              const reconstructed: Record<string, unknown> = { ...stream.stream, message: line };
              parsed = reconstructed;
              if (typeof reconstructed.level === 'string') level = reconstructed.level.toUpperCase();
            }

            return { ts, line, level, parsed, container, syslogPrefix };
          })
        ).sort((a, b) => Number(b.ts) - Number(a.ts)); // Descending

        setLogs(flatLogs);
      } else {
        setLogs([]);
      }
    } catch (err: any) {
      console.error(err);
      if (!autoRefresh) setError('Failed to fetch logs. Ensure Loki is running and accessible.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    let interval: any;
    if (autoRefresh) {
      interval = setInterval(fetchLogs, 5000);
    }
    return () => clearInterval(interval);
  }, [source, customSource, sourceType, service, levelFilter, limit, autoRefresh, timeRange]);

  const handleSearch = () => {
    fetchLogs();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  // Calculate log level counts
  const levelCounts = {
    critical: logs.filter(l => l.level === 'CRITICAL').length,
    error: logs.filter(l => l.level === 'ERROR').length,
    warning: logs.filter(l => l.level === 'WARN' || l.level === 'WARNING').length,
    info: logs.filter(l => l.level === 'INFO').length,
    debug: logs.filter(l => l.level === 'DEBUG').length,
  };

  const handleExportCSV = () => {
    const headers = ['Timestamp', 'Level', 'Source', 'Message'];
    const rows = logs.map(log => {
      const date = new Date(Number(log.ts) / 1000000);
      const settings = useSettingsStore.getState().getDateFormatSettings();
      return [
        formatDate(date, settings),
        log.level,
        log.container || 'unknown',
        log.line.replace(/"/g, '""') // Escape quotes
      ];
    });

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const exportData = logs.map(log => {
      const date = new Date(Number(log.ts) / 1000000);
      const settings = useSettingsStore.getState().getDateFormatSettings();
      return {
        timestamp: formatDate(date, settings),
        level: log.level,
        container: log.container || 'unknown',
        message: log.line,
        parsed: log.parsed
      };
    });

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeSource = customSource || source;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">System Logs</h1>
        <p className="text-gray-600 mt-1">Unified log viewer for all system components and edge devices</p>
      </div>

      {/* Log Level Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Critical</p>
              <p className="text-2xl font-bold text-danger-600 mt-1">{levelCounts.critical}</p>
            </div>
            <div className="w-10 h-10 bg-danger-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-danger-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Error</p>
              <p className="text-2xl font-bold text-danger-600 mt-1">{levelCounts.error}</p>
            </div>
            <div className="w-10 h-10 bg-danger-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-danger-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Warning</p>
              <p className="text-2xl font-bold text-warning-600 mt-1">{levelCounts.warning}</p>
            </div>
            <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-warning-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Info</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{levelCounts.info}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Debug</p>
              <p className="text-2xl font-bold text-gray-600 mt-1">{levelCounts.debug}</p>
            </div>
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        {/* Source Type Selector */}
        <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-gray-200">
          <label className="text-sm font-medium text-gray-700">Source Type:</label>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setSourceType('edge');
                if (availableEdges.length > 0) {
                  setSource(availableEdges[0].id);
                }
                setCustomSource('');
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${sourceType === 'edge'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Edge Devices
            </button>
            <button
              onClick={() => {
                setSourceType('mtge');
                if (availableMtges.length > 0) {
                  setSource(availableMtges[0].id);
                }
                setCustomSource('');
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${sourceType === 'mtge'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Shared Gateways
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Logs</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search message or details..."
                className="input-base w-full pl-10"
              />
            </div>
          </div>

          {/* Log Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Log Level</label>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value as LogLevel)}
              className="input-base w-full"
            >
              <option value="ALL">All Levels</option>
              <option value="CRITICAL">Critical</option>
              <option value="ERROR">Error</option>
              <option value="WARN">Warning</option>
              <option value="INFO">Info</option>
              <option value="DEBUG">Debug</option>
            </select>
          </div>

          {/* Source: Edge or MTGE */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {sourceType === 'mtge' ? 'Gateway' : 'Edge'}
            </label>
            <select
              value={customSource ? 'custom' : source}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  // Keep custom source as is
                } else {
                  setSource(e.target.value);
                  setCustomSource('');
                }
              }}
              className="input-base w-full"
            >
              {sourceType === 'edge' ? (
                availableEdges.length > 0 ? (
                  availableEdges.map(edge => (
                    <option key={edge.id} value={edge.id}>{edge.name}</option>
                  ))
                ) : (
                  <option value="">No edges found</option>
                )
              ) : (
                availableMtges.length > 0 ? (
                  availableMtges.map(mtge => (
                    <option key={mtge.id} value={mtge.id}>{mtge.name}</option>
                  ))
                ) : (
                  <option value="">No shared gateways found</option>
                )
              )}
            </select>
          </div>

          {/* Service (Edge/MTGE Only) */}
          {(sourceType === 'edge' || sourceType === 'mtge') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Service</label>
              <select
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="input-base w-full"
              >
                <option value="all">All Services</option>
                {activeServices.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="input-base w-full"
            >
              <option value="5m">Last 5 min</option>
              <option value="15m">Last 15 min</option>
              <option value="1h">Last 1 hour</option>
              <option value="6h">Last 6 hours</option>
              <option value="24h">Last 24 hours</option>
            </select>
          </div>

          {/* Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Limit</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="input-base w-full"
            >
              <option value={100}>100 lines</option>
              <option value={500}>500 lines</option>
              <option value={1000}>1000 lines</option>
              <option value={2000}>2000 lines</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{logs.length}</span> logs
            {logs.length >= limit && <span className="text-warning-600"> (limit reached)</span>}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`btn-secondary inline-flex items-center space-x-2 ${autoRefresh ? 'bg-green-50 text-green-700 border-green-200' : ''
                }`}
            >
              {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{autoRefresh ? 'Live' : 'Paused'}</span>
            </button>
            <button
              onClick={handleSearch}
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleExportCSV}
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>CSV</span>
            </button>
            <button
              onClick={handleExportJSON}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>JSON</span>
            </button>
          </div>
        </div>
      </div>

      {/* Info Banner for Edge Logs */}
      {sourceType === 'edge' && logs.length === 0 && !loading && !error && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Edge Logs Not Found</h3>
            <p className="mt-1 text-sm text-blue-700">
              No logs found for edge_id "{activeSource}". This may mean:
            </p>
            <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
              <li>The edge hasn't generated logs recently (check time range)</li>
              <li>Promtail is not deployed on the edge device</li>
              <li>The agent-mqtt-cli needs to be rebuilt with edge_id logging support</li>
            </ul>
            <p className="mt-2 text-sm text-blue-700">
              See <code className="bg-blue-100 px-1 py-0.5 rounded">EDGE_LOGGING_ARCHITECTURE.md</code> for deployment instructions.
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error fetching logs</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="card">
        {logs.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-500">
              No logs found. {activeSource && activeSource !== 'all'
                ? `Try adjusting the time range or service filter for this device.`
                : `Select a device to view logs.`}
            </p>
          </div>
        )}

        {logs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log, index) => {
                  const date = new Date(Number(log.ts) / 1000000);
                  const isExpanded = expandedRows.has(index);
                  const hasDetails = log.parsed || log.line.length > 100;

                  let levelColor = 'bg-gray-100 text-gray-700 border-gray-200';
                  if (log.level === 'CRITICAL') levelColor = 'bg-danger-100 text-danger-700 border-danger-200';
                  else if (log.level === 'ERROR') levelColor = 'bg-danger-100 text-danger-700 border-danger-200';
                  else if (log.level === 'WARN' || log.level === 'WARNING') levelColor = 'bg-warning-100 text-warning-700 border-warning-200';
                  else if (log.level === 'INFO') levelColor = 'bg-blue-100 text-blue-700 border-blue-200';
                  else if (log.level === 'DEBUG') levelColor = 'bg-gray-100 text-gray-700 border-gray-200';

                  return (
                    <React.Fragment key={index}>
                      <tr className="hover:bg-gray-50">
                        <td
                          onClick={() => hasDetails && toggleRow(index)}
                          className={`px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600 ${hasDetails ? 'cursor-pointer hover:text-primary-600 transition-colors' : ''}`}
                        >
                          {formatDate(date, useSettingsStore.getState().getDateFormatSettings())}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${levelColor}`}>
                            {log.level}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {log.container || 'unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-2xl">
                          <div className="truncate">
                            {log.parsed?.message || log.parsed?.msg || (() => {
                              // Strip syslog prefix for cleaner display
                              if (log.syslogPrefix) {
                                const jsonPart = log.line.substring(log.syslogPrefix.length).replace(/^\s*\|?\s*/, '');
                                return jsonPart.substring(0, 200);
                              }
                              return log.line.substring(0, 200);
                            })()}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {hasDetails && (
                            <button
                              onClick={() => toggleRow(index)}
                              className="inline-flex items-center space-x-1 text-primary-600 hover:text-primary-700"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="w-4 h-4" />
                                  <span>Hide</span>
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4" />
                                  <span>Show</span>
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && hasDetails && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 bg-gray-50">
                            <div className="space-y-2">
                              {log.syslogPrefix && (
                                <div className="text-xs text-gray-500 font-mono">{log.syslogPrefix}</div>
                              )}
                              {log.parsed ? (
                                <div className="bg-white rounded border border-gray-200 overflow-hidden">
                                  <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-500 uppercase">Structured Log</span>
                                    {log.parsed.level && (
                                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${log.parsed.level === 'error' ? 'bg-red-100 text-red-700' :
                                        log.parsed.level === 'warn' || log.parsed.level === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                          log.parsed.level === 'debug' ? 'bg-gray-100 text-gray-600' :
                                            'bg-blue-100 text-blue-700'
                                        }`}>{log.parsed.level}</span>
                                    )}
                                  </div>
                                  <div className="divide-y divide-gray-100">
                                    {Object.entries(log.parsed).map(([key, value]) => {
                                      const isMessage = key === 'message' || key === 'msg';
                                      const isTimestamp = key === 'timestamp' || key === 'time' || key === 'ts';
                                      const isLevel = key === 'level';
                                      return (
                                        <div key={key} className={`flex px-3 py-1.5 text-xs ${isMessage ? 'bg-blue-50' : ''}`}>
                                          <span className={`font-mono font-semibold w-40 flex-shrink-0 ${isMessage ? 'text-blue-700' :
                                            isTimestamp ? 'text-purple-600' :
                                              isLevel ? 'text-orange-600' :
                                                'text-gray-500'
                                            }`}>{key}</span>
                                          <span className={`font-mono break-all ${isMessage ? 'text-blue-900 font-medium' : 'text-gray-800'
                                            }`}>
                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Raw Log</div>
                                  <pre className="text-xs text-gray-700 bg-white p-3 rounded border border-gray-200 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all">
                                    {log.line}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <RefreshCw className="inline-block h-8 w-8 text-gray-400 animate-spin" />
            <p className="mt-2 text-sm text-gray-500">Loading logs...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Logs;
